import React, { useState, useContext } from "react";

import { motion, AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import Markdown from "../../styles/Markdown";

const Container = styled.div`
  position: relative;
  height: ${(props) => props.height || "100%"};
  width: 100%;
  margin-top: ${(props) => props.offset || "0px"};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => props.objectFit || "cover"};
`;

const TextOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Text = styled.span`
  color: white;
  font-size: 24px;
  font-weight: bold;
`;

const DarkOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

function ImageWithText({
  photoUrl,
  initialText,
  hoverText,
  offset,
  objectFit,
  hasOverlayOnHover,
}) {
  const { boxHeight } = useContext(ComponentContext);

  const [isHovered, setIsHovered] = useState(false);

  const initialVariants = {
    visible: { opacity: 1, y: 0 },
    hidden: { opacity: 0, y: "-10%" },
  };

  const hoverVariants = {
    hidden: { opacity: 0, y: "50%" },
    visible: { opacity: 1, y: 0 },
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  return (
    <Container
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      height={boxHeight}
      offset={offset}
      className="image-with-text"
    >
      <Image src={photoUrl} alt="Background" objectFit={objectFit} />
      <AnimatePresence>
        {isHovered && hasOverlayOnHover ? (
          <DarkOverlay
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={overlayVariants}
            transition={{ duration: 0.3 }}
            className="image-overlay"
          />
        ) : null}
      </AnimatePresence>
      <AnimatePresence>
        {!isHovered && (
          <TextOverlay
            initial="visible"
            animate="visible"
            exit="hidden"
            variants={initialVariants}
            transition={{ duration: 0.3 }}
          >
            <Text>
              <Markdown>{initialText}</Markdown>
            </Text>
          </TextOverlay>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isHovered && (
          <TextOverlay
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={hoverVariants}
            transition={{ duration: 0.3 }}
          >
            <Text>
              <Markdown>{hoverText}</Markdown>
            </Text>
          </TextOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
}

export default ImageWithText;

ImageWithText.defaultProps = {
  photoUrl: "",
  initialText: "",
  hoverText: "",
  objectFit: "cover",
  offset: "0px",
  hasOverlayOnHover: false,
};

ImageWithText.propTypes = {
  photoUrl: PropTypes.string,
  initialText: PropTypes.string,
  hoverText: PropTypes.string,
  objectFit: PropTypes.string,
  offset: PropTypes.string,
  hasOverlayOnHover: PropTypes.bool,
};
